import React from 'react';
import { Grid, Typography } from '@mui/material';
import { useServicesEnabled } from 'core/contexts';
import { isStatusCodeSuccessful } from '../../core';
import { toLocalDateTimeString } from '../../lib';
import { DashboardCardHeader } from './components/dashboard-card-header/DashboardCardHeader';
import { ChainMetrics } from './components/dashboard-chart/dashboard-chart-helpers';
import { DashboardContainer } from './components/dashboard-chart/DashboardContainer';
import { DashboardEmptyState } from './components/dashboard-empty-state/DashboardEmptyState';
import { DashboardMetricCards } from './components/dashboard-metric-cards/DashboardMetricCards';
import { dashboardStyles, Metrics } from './dashboard-helper';
import { HelixLoader, PageWrapper } from 'components';
import { useDashboardMetrics } from 'hooks';

export const Dashboard = (): React.JSX.Element => {
    const { isServiceEnabledFor } = useServicesEnabled();
    const {
        data: dashboardMetricsQuery,
        isLoading: dashboardMetricsQueryLoading,
        refetch: refreshMetricsData,
    } = useDashboardMetrics();

    const chainMetrics: ChainMetrics =
        dashboardMetricsQuery?.data?.chainMetrics ?? {};
    const totalNumberOfChains: number = Object.keys(chainMetrics).length ?? 0;
    const lastUpdatedTimestamp: string | undefined =
        dashboardMetricsQuery?.data?.lastUpdated;
    const normalizedTimestamp: string = lastUpdatedTimestamp
        ? toLocalDateTimeString(lastUpdatedTimestamp, true)
        : 'N/A';
    const isBlobsServiceEnabled = isServiceEnabledFor('BLOBS');
    const metricsData: Metrics | undefined = dashboardMetricsQuery?.data;

    // Check for a valid empty data response status.
    const isEmptyData: boolean =
        !metricsData || isStatusCodeSuccessful(metricsData?.status ?? 0);

    const renderMetrics = (): React.JSX.Element => {
        if (dashboardMetricsQueryLoading) {
            return <HelixLoader />;
        }

        if (isEmptyData) {
            return <DashboardEmptyState refreshData={refreshMetricsData} />;
        }

        return (
            <>
                <DashboardMetricCards
                    dashboardMetricsQueryLoading={dashboardMetricsQueryLoading}
                    isBlobsServiceEnabled={isBlobsServiceEnabled}
                    totalNumberOfChains={totalNumberOfChains}
                    metrics={metricsData}
                />
                <DashboardContainer data={chainMetrics} />
            </>
        );
    };

    return (
        <PageWrapper>
            <DashboardCardHeader />
            <Grid sx={dashboardStyles.mainContainer}>
                <Grid
                    sx={
                        isEmptyData
                            ? {
                                  ...dashboardStyles.titleContainer,
                                  ...dashboardStyles.emptyTitleContainerSpacing,
                              }
                            : {
                                  ...dashboardStyles.titleContainer,
                                  ...dashboardStyles.titleContainerSpacing,
                              }
                    }
                >
                    <Typography sx={dashboardStyles.title}>Metrics</Typography>
                    {lastUpdatedTimestamp && (
                        <Typography
                            sx={dashboardStyles.timestamp}
                        >{`Last Updated: ${normalizedTimestamp}`}</Typography>
                    )}
                </Grid>
                {renderMetrics()}
            </Grid>
        </PageWrapper>
    );
};
